import React, { useEffect, useMemo, useState } from "react";
import Icon from "@mdi/react";
import { mdiClose } from "@mdi/js";
import { Button, CircularProgress, Switch } from "@mui/material";
import styles from "./styles.module.scss";
import { useParams } from "react-router-dom";
import { MEMBER_STATUS_MAP } from "./mockData";
import { PublicTeamMemberDTO, WorkspaceServices, NotistackServices } from "shared/services";
import { HandleError } from "shared/utils";

interface ToolManagementContentProps {
	members: PublicTeamMemberDTO[];
	workspaceUid: string | null;
	onUpdate?: () => void;
}

/** Gera iniciais a partir do nome (até 2 letras). */
function getInitials(name: string): string {
	return name
		.split(/[\s-_]+/)
		.filter(Boolean)
		.map((w) => w[0].toUpperCase())
		.slice(0, 2)
		.join("");
}

export const ToolManagementContent: React.FC<ToolManagementContentProps> = ({ members, workspaceUid, onUpdate }) => {
	const { toolId } = useParams<{ toolId: string }>();

	const selectedMember = useMemo(() => members.find((m) => String(m.id) === toolId) ?? members[0] ?? null, [toolId, members]);

	const mcp = selectedMember?.contractedMcp?.mcp ?? selectedMember?.mcp ?? null;
	const toolEnvFields = mcp?.toolEnvFields ?? [];
	const contractedConfig = selectedMember?.contractedMcp?.config ?? {};

	// Local state for form fields
	const [memberName, setMemberName] = useState("");
	const [instructions, setInstructions] = useState("");
	const [toolEnvValues, setToolEnvValues] = useState<Record<string, any>>({});
	const [saving, setSaving] = useState(false);

	// Sync form when member changes
	useEffect(() => {
		if (!selectedMember) return;
		setMemberName(selectedMember.name);
		setInstructions(selectedMember.customSystemPrompt ?? "");
		setToolEnvValues({ ...contractedConfig });
	}, [selectedMember]);

	const handleToolEnvChange = (envName: string, value: any) => {
		setToolEnvValues((prev) => ({ ...prev, [envName]: value }));
	};

	const handleSave = async () => {
		if (!selectedMember || !workspaceUid) return;
		setSaving(true);
		try {
			await WorkspaceServices.updateMember(workspaceUid, selectedMember.id as any, {
				name: memberName,
				custom_system_prompt: instructions || null,
				config: toolEnvValues,
			});
			NotistackServices.showSuccessMessage("Configurações salvas com sucesso");
			onUpdate?.();
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao salvar configurações");
		} finally {
			setSaving(false);
		}
	};

	const handleRemove = async () => {
		if (!selectedMember || !workspaceUid) return;
		if (!window.confirm(`Deseja realmente remover "${selectedMember.name}"?`)) return;
		try {
			await WorkspaceServices.removeMember(workspaceUid, selectedMember.id as any);
			NotistackServices.showSuccessMessage("Ferramenta removida com sucesso");
			onUpdate?.();
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao remover ferramenta");
		}
	};

	if (!selectedMember) {
		return (
			<div className={styles.managementContainer}>
				<div className={styles.loadingContainer}>
					<p>Selecione uma ferramenta no painel lateral</p>
				</div>
			</div>
		);
	}

	const statusInfo = !selectedMember.isActive
		? { label: "Inativo", className: "statusInactive" }
		: (MEMBER_STATUS_MAP[selectedMember.status] ?? { label: selectedMember.status, className: "statusActive" });

	return (
		<div className={styles.managementContainer}>
			{/* Header & Metrics */}
			<div className={styles.headerSection}>
				<div className={styles.headerLeft}>
					<div
						className={styles.toolLogo}
						style={{ backgroundColor: "var(--mui-palette-primary-main)" }}
					>
						<span>{getInitials(mcp?.name ?? selectedMember.name)}</span>
					</div>
					<div>
						<h1 className={styles.toolName}>{mcp?.name ?? selectedMember.name}</h1>
						<div className={styles.connectionStatus}>
							<span className={styles.statusDot} />
							<span>{statusInfo.label}</span>
						</div>
					</div>
				</div>
				<div className={styles.metricsRow}>
					<div className={styles.metricCard}>
						<p className={styles.metricLabel}>Senioridade</p>
						<p className={styles.metricValue}>{selectedMember.seniority}</p>
					</div>
					<div className={styles.metricCard}>
						<p className={styles.metricLabel}>Capacidade usada</p>
						<p className={styles.metricValue}>{selectedMember.capacidadeUsada}%</p>
					</div>
					<div className={styles.metricCard}>
						<p className={styles.metricLabel}>Categoria</p>
						<p className={styles.metricValue}>{mcp?.category ?? "—"}</p>
					</div>
				</div>
			</div>

			{/* Form */}
			<div className={styles.formBody}>
				{/* Name & MCP info */}
				<div className={styles.formGrid}>
					<div className={styles.formGroup}>
						<label className={styles.formLabel}>Nome do Membro</label>
						<input
							className={styles.formInput}
							type="text"
							value={memberName}
							onChange={(e) => setMemberName(e.target.value)}
							placeholder="Ex: Gestor-Docs"
						/>
					</div>
					<div className={styles.formGroup}>
						<label className={styles.formLabel}>MCP vinculado</label>
						<input
							className={styles.formInput}
							type="text"
							value={mcp?.name ?? "—"}
							readOnly
						/>
					</div>
				</div>

				{/* ENV Fields */}
				{toolEnvFields.length > 0 && (
					<div className={styles.envSection}>
						<h3 className={styles.sectionTitle}>CAMPOS ENV DA FERRAMENTA</h3>
						<div className={styles.envGrid}>
							{toolEnvFields.map((field) => {
								const currentValue = toolEnvValues[field.envName];
								const isOAuth2 = field.type.startsWith("OAuth2:");

								return (
									<div
										key={field.envName}
										className={styles.envFieldCard}
									>
										<div className={styles.envFieldHeader}>
											<label className={styles.formLabel}>
												{field.label}
												{field.required ? " *" : ""}
											</label>
											<span className={styles.envType}>{field.type}</span>
										</div>

										{field.type === "boolean" ? (
											<div className={styles.sensitiveRow}>
												<span className={styles.sensitiveLabel}>{field.description}</span>
												<Switch
													checked={Boolean(currentValue)}
													onChange={(event) => handleToolEnvChange(field.envName, event.target.checked)}
													color="primary"
													size="small"
												/>
											</div>
										) : field.type === "json" ? (
											<textarea
												className={styles.formTextarea}
												rows={4}
												placeholder={field.description}
												value={typeof currentValue === "string" ? currentValue : JSON.stringify(currentValue ?? {}, null, 2)}
												onChange={(event) => handleToolEnvChange(field.envName, event.target.value)}
											/>
										) : (
											<input
												className={styles.formInput}
												type={field.type === "number" ? "number" : "text"}
												placeholder={isOAuth2 ? `Token/Client para ${field.type.replace("OAuth2:", "")}` : field.description}
												value={currentValue ?? ""}
												onChange={(event) => handleToolEnvChange(field.envName, event.target.value)}
											/>
										)}
										{field.description && <p className={styles.envDescription}>{field.description}</p>}
									</div>
								);
							})}
						</div>
					</div>
				)}

				{/* Permanent instructions */}
				<div className={styles.formGroup}>
					<label className={styles.formLabel}>Instruções permanentes ao agente</label>
					<textarea
						className={styles.formTextarea}
						value={instructions}
						onChange={(e) => setInstructions(e.target.value)}
						placeholder="Instruções que o agente deve seguir sempre ao usar esta ferramenta..."
						rows={5}
					/>
				</div>
			</div>

			{/* Footer Actions */}
			<div className={styles.footerActions}>
				<Button
					variant="contained"
					color="primary"
					className={styles.saveBtn}
					disableElevation
					onClick={handleSave}
					disabled={saving}
					startIcon={
						saving ? (
							<CircularProgress
								size={16}
								color="inherit"
							/>
						) : undefined
					}
				>
					{saving ? "Salvando..." : "Salvar configurações"}
				</Button>
				<Button
					variant="outlined"
					color="error"
					className={styles.removeBtn}
					onClick={handleRemove}
				>
					Remover ferramenta
				</Button>
			</div>
		</div>
	);
};
